import { afterNextRender, inject, Injectable, Injector } from "@angular/core";
import { BehaviorSubject, firstValueFrom, isObservable } from "rxjs";
import { IStringLookup } from "@logex/framework/types";
import {
    LgSlideoutApi,
    LgSlideoutPanel,
    LgSlideoutState,
    LgSlideoutVariant
} from "./lg-slideout.types";

type VariantLookup<T> = Record<LgSlideoutVariant, T>;
type SlideoutSlot = "pinned" | "overlay";

@Injectable({ providedIn: "root" })
export class LgSlideoutService {
    private _injector = inject(Injector);

    private _primaryHolderApis: VariantLookup<IStringLookup<LgSlideoutApi>> = {
        left: {},
        right: {}
    };

    private _overlayHolderApis: VariantLookup<IStringLookup<LgSlideoutApi>> = {
        left: {},
        right: {}
    };

    private _availablePanelsKeys: string[] = [];

    public currentPanelVariant: string | null = null;
    public currentOverlayPanelVariant: string | null = null;
    public availablePanels: Record<string, LgSlideoutPanel> = {};
    public availablePanelsState$ = new BehaviorSubject(this.availablePanels);
    get activePanelVariant(): string | null {
        return this.currentPanelVariant;
    }

    getAvailablePanel(panelVariant: string): LgSlideoutPanel {
        return this.availablePanels[panelVariant];
    }

    addAvailablePanel(panel: LgSlideoutPanel): void {
        Promise.resolve().then(() => {
            this.availablePanels[panel.panelVariant] = panel;
            this.availablePanelsState$.next(this.availablePanels);
        });
    }

    deletePanel(panelVariant: string): void {
        delete this.availablePanels[panelVariant];
        this.availablePanelsState$.next(this.availablePanels);
        this._availablePanelsKeys = this._availablePanelsKeys.filter(key => key !== panelVariant);
    }

    api(id: string, slideoutVariant: LgSlideoutVariant): LgSlideoutApi {
        if (this._primaryHolderApis[slideoutVariant][id]) {
            return this._primaryHolderApis[slideoutVariant][id];
        }

        let state: LgSlideoutState = {
            pinned: false,
            expanded: false,
            hidePin: false,
            template: undefined,
            templateContext: undefined
        };

        let slideoutVisible = false;

        const state$ = new BehaviorSubject<LgSlideoutState>(state);

        const api: LgSlideoutApi = {
            state$: state$.asObservable(),
            togglePinned: (pinned?: boolean): void => {
                if (state.pinned === pinned) return;
                const newVal = pinned == null ? !state.pinned : pinned;
                state = { ...state, pinned: newVal };
                state$.next(state);
            },
            togglePinVisibility: (hidden = false): void => {
                state = { ...state, hidePin: hidden };
                state$.next(state);
            },
            toggleExpanded: async (expanded?: boolean): Promise<void> => {
                if (state.expanded === expanded) return;
                const newVal = expanded == null ? !state.expanded : expanded;

                if (!newVal) {
                    const canClose = await this._canClose("pinned");
                    if (!canClose) return;
                }

                state = { ...state, expanded: newVal, pinned: newVal ? state.pinned : false };
                if (!newVal) {
                    this._closeAllPanels(id, slideoutVariant);
                    this._overlayHolderApis[slideoutVariant][id]?.toggleExpanded(false);
                }

                state$.next(state);
            },
            setActivePanel: async (panelVariant: string): Promise<void> => {
                if (this.currentPanelVariant !== panelVariant) {
                    if (state.expanded) {
                        const canClose = await this._canClose("pinned");
                        if (!canClose) {
                            return;
                        }
                    }
                    this._setActivePanel(panelVariant, slideoutVisible, "pinned");
                }
                const panel = this.availablePanels[panelVariant];
                state = {
                    ...state,
                    template: panel?.panelTemplate,
                    templateContext: panel?.panelTemplateContext,
                    hidePin: panel?.hidePin ?? false,
                    panelWidth: panel?.width,
                    suppressBackdropClose: panel?.suppressBackdropClose ?? false
                };
                state$.next(state);
            },
            set: (
                newState: Pick<LgSlideoutState, "expanded" | "pinned" | "hidePin">,
                panelVariant: string
            ): void => {
                const panel = this.availablePanels[panelVariant];
                state = {
                    ...newState,
                    template: panel?.panelTemplate,
                    templateContext: panel?.panelTemplateContext,
                    panelWidth: panel?.width,
                    suppressBackdropClose: panel?.suppressBackdropClose ?? false
                };

                if (!state.expanded) {
                    this._closeAllPanels(id, slideoutVariant);
                    this._overlayHolderApis[slideoutVariant][id]?.toggleExpanded(false);
                }

                state$.next(state);
            },
            showPanel: async (panelVariant: string): Promise<void> => {
                const panel = this.availablePanels[panelVariant];
                if (!panel) {
                    return;
                }
                // When pinned holder is pinned and expanded, route new panel to overlay
                if (state.pinned && state.expanded && this.currentPanelVariant !== panelVariant) {
                    await this._createOverlayApi(id, slideoutVariant).showPanel(panelVariant);
                    return;
                }
                if (this.currentPanelVariant !== panelVariant) {
                    if (state.expanded) {
                        const canClose = await this._canClose("pinned");
                        if (!canClose) return;
                    }
                    this._setActivePanel(panel.panelVariant, slideoutVisible, "pinned");
                }
                state = {
                    ...state,
                    expanded: true,
                    template: panel.panelTemplate,
                    templateContext: panel.panelTemplateContext,
                    hidePin: panel.hidePin ?? false,
                    panelWidth: panel.width,
                    suppressBackdropClose: panel.suppressBackdropClose ?? false
                };
                state$.next(state);
            },
            hidePanel: async (panelVariant: string, unpin: boolean): Promise<boolean> => {
                if (!state.expanded || this.currentPanelVariant !== panelVariant) {
                    // Delegate to overlay if the panel is active there
                    const overlayApi = this._overlayHolderApis[slideoutVariant][id];
                    if (overlayApi && this.currentOverlayPanelVariant === panelVariant) {
                        return overlayApi.hidePanel(panelVariant, unpin);
                    }
                    return false;
                }
                if (!unpin && state.pinned) {
                    return false;
                }
                const canClose = await this._canClose("pinned");
                if (!canClose) {
                    return false;
                }

                state = { ...state, expanded: false, pinned: false };
                this._closeAllPanels(id, slideoutVariant);

                state$.next(state);
                return true;
            },
            slideoutShown: (): void => {
                if (!slideoutVisible) {
                    slideoutVisible = true;
                    const panel = this.availablePanels[this.currentPanelVariant ?? ""];
                    panel?.componentChangeDetector?.markForCheck();
                    if (
                        panel &&
                        state.template === panel.panelTemplate &&
                        panel.onVisibilityChange
                    ) {
                        afterNextRender(
                            () => {
                                panel!.onVisibilityChange?.(true);
                            },
                            { injector: this._injector }
                        );
                    }
                }
            },
            slideoutHidden: (): void => {
                if (slideoutVisible) {
                    slideoutVisible = false;
                    const panel = this.availablePanels[this.currentPanelVariant ?? ""];
                    panel?.componentChangeDetector?.markForCheck();
                    if (
                        panel &&
                        state.template === panel.panelTemplate &&
                        panel.onVisibilityChange
                    ) {
                        afterNextRender(
                            () => {
                                panel!.onVisibilityChange?.(false);
                            },
                            { injector: this._injector }
                        );
                    }
                }
            }
        };

        this._primaryHolderApis[slideoutVariant][id] = api;

        return api;
    }

    overlayApi(id: string, slideoutVariant: LgSlideoutVariant): LgSlideoutApi {
        return this._createOverlayApi(id, slideoutVariant);
    }

    setAvailablePanels(availablePanelsKeys: string[]): void {
        this._availablePanelsKeys = availablePanelsKeys;
    }

    getAvailablePanels(): string[] {
        return this._availablePanelsKeys;
    }

    private async _canClose(slot: SlideoutSlot = "overlay"): Promise<boolean> {
        const variant =
            slot === "pinned" ? this.currentPanelVariant : this.currentOverlayPanelVariant;
        const panel = this.availablePanels[variant ?? ""];
        if (!panel?.onBeforeClose) {
            return true;
        }
        const result = panel.onBeforeClose();
        if (result instanceof Promise) {
            return result;
        }
        if (isObservable(result)) {
            return firstValueFrom(result);
        }
        return result;
    }

    private _setActivePanel(
        panelVariant: string | null,
        slideoutVisible: boolean,
        slot: SlideoutSlot = "overlay"
    ): void {
        const currentVariant =
            slot === "pinned" ? this.currentPanelVariant : this.currentOverlayPanelVariant;
        const oldPanel = this.availablePanels[currentVariant ?? ""];
        if (oldPanel) {
            if (
                slideoutVisible &&
                (oldPanel.onVisibilityChange || oldPanel.componentChangeDetector)
            ) {
                afterNextRender(
                    () => {
                        oldPanel.componentChangeDetector?.markForCheck();
                        const callback = oldPanel.onVisibilityChange;
                        if (callback) {
                            afterNextRender(() => callback(false), { injector: this._injector });
                        }
                    },
                    { injector: this._injector }
                );
            }
            if (slot === "pinned") {
                oldPanel.isActive = false;
            }
        }

        if (panelVariant && this.availablePanels[panelVariant] != null) {
            const newPanel = this.availablePanels[panelVariant];
            if (
                slideoutVisible &&
                (newPanel.onVisibilityChange || newPanel.componentChangeDetector)
            ) {
                afterNextRender(
                    () => {
                        newPanel.componentChangeDetector?.markForCheck();
                        const callback = newPanel.onVisibilityChange;
                        if (callback) {
                            afterNextRender(() => callback(true), { injector: this._injector });
                        }
                    },
                    { injector: this._injector }
                );
            }
            if (slot === "pinned") {
                newPanel.isActive = true;
            }
        }

        if (slot === "pinned") {
            this.currentPanelVariant = panelVariant;
        } else {
            this.currentOverlayPanelVariant = panelVariant;
        }
        this.availablePanelsState$.next(this.availablePanels);
    }

    private _closeAllPanels(exceptId: string, slideoutVariant: LgSlideoutVariant): void {
        Object.keys(this._primaryHolderApis[slideoutVariant]).forEach(id => {
            if (id === exceptId) return;

            this._primaryHolderApis[slideoutVariant][id].toggleExpanded(false);
        });
    }

    private _createOverlayApi(baseId: string, slideoutVariant: LgSlideoutVariant): LgSlideoutApi {
        if (this._overlayHolderApis[slideoutVariant][baseId])
            return this._overlayHolderApis[slideoutVariant][baseId];

        let state: LgSlideoutState = {
            pinned: false,
            expanded: false,
            hidePin: false,
            template: undefined,
            templateContext: undefined
        };

        let slideoutVisible = false;

        const state$ = new BehaviorSubject<LgSlideoutState>(state);

        const api: LgSlideoutApi = {
            state$: state$.asObservable(),
            togglePinned: (pinned?: boolean): void => {
                if (state.pinned === pinned) return;
                const newVal = pinned == null ? !state.pinned : pinned;
                if (newVal) {
                    // Move current overlay panel to pinned slot
                    const overlayPanelVariant = this.currentOverlayPanelVariant;
                    if (overlayPanelVariant) {
                        const primaryApi = this.api(baseId, slideoutVariant);
                        primaryApi.set(
                            { expanded: true, pinned: true, hidePin: false },
                            overlayPanelVariant
                        );
                    }
                    // Close overlay
                    state = { ...state, expanded: false, pinned: false };
                } else {
                    state = { ...state, pinned: false };
                }
                state$.next(state);
            },
            togglePinVisibility: (hidden = false): void => {
                state = { ...state, hidePin: hidden };
                state$.next(state);
            },
            toggleExpanded: async (expanded?: boolean): Promise<void> => {
                if (state.expanded === expanded) return;
                const newVal = expanded == null ? !state.expanded : expanded;

                if (!newVal) {
                    const canClose = await this._canClose("overlay");
                    if (!canClose) return;
                }

                state = { ...state, expanded: newVal, pinned: newVal ? state.pinned : false };
                if (!newVal) {
                    this._setActivePanel(null, slideoutVisible, "overlay");
                }

                state$.next(state);
            },
            setActivePanel: async (panelVariant: string): Promise<void> => {
                if (this.currentOverlayPanelVariant !== panelVariant) {
                    if (state.expanded) {
                        const canClose = await this._canClose("overlay");
                        if (!canClose) return;
                    }
                    this._setActivePanel(panelVariant, slideoutVisible, "overlay");
                }
                const panel = this.availablePanels[panelVariant];
                state = {
                    ...state,
                    template: panel?.panelTemplate,
                    templateContext: panel?.panelTemplateContext,
                    hidePin: panel?.hidePin ?? false,
                    panelWidth: panel?.width,
                    suppressBackdropClose: panel?.suppressBackdropClose ?? false
                };
                state$.next(state);
            },
            set: (
                newState: Pick<LgSlideoutState, "expanded" | "pinned" | "hidePin">,
                panelVariant: string
            ): void => {
                const panel = this.availablePanels[panelVariant];
                state = {
                    ...newState,
                    template: panel?.panelTemplate,
                    templateContext: panel?.panelTemplateContext,
                    panelWidth: panel?.width,
                    suppressBackdropClose: panel?.suppressBackdropClose ?? false
                };
                if (!state.expanded) {
                    this._setActivePanel(null, slideoutVisible, "overlay");
                }
                state$.next(state);
            },
            showPanel: async (panelVariant: string): Promise<void> => {
                const panel = this.availablePanels[panelVariant];
                if (!panel) return;
                if (this.currentOverlayPanelVariant !== panelVariant) {
                    if (state.expanded) {
                        const canClose = await this._canClose("overlay");
                        if (!canClose) return;
                    }
                    this._setActivePanel(panel.panelVariant, slideoutVisible, "overlay");
                }
                state = {
                    ...state,
                    expanded: true,
                    template: panel.panelTemplate,
                    templateContext: panel.panelTemplateContext,
                    hidePin: panel.hidePin ?? false,
                    panelWidth: panel.width,
                    suppressBackdropClose: panel.suppressBackdropClose ?? false
                };
                state$.next(state);
            },
            hidePanel: async (panelVariant: string, unpin: boolean): Promise<boolean> => {
                if (!state.expanded || this.currentOverlayPanelVariant !== panelVariant) {
                    return false;
                }
                if (!unpin && state.pinned) {
                    return false;
                }
                const canClose = await this._canClose("overlay");
                if (!canClose) return false;

                state = { ...state, expanded: false, pinned: false };
                this._setActivePanel(null, slideoutVisible, "overlay");
                state$.next(state);
                return true;
            },
            slideoutShown: (): void => {
                if (!slideoutVisible) {
                    slideoutVisible = true;
                    const panel = this.availablePanels[this.currentOverlayPanelVariant ?? ""];
                    panel?.componentChangeDetector?.markForCheck();
                    if (
                        panel &&
                        state.template === panel.panelTemplate &&
                        panel.onVisibilityChange
                    ) {
                        afterNextRender(
                            () => {
                                panel!.onVisibilityChange?.(true);
                            },
                            { injector: this._injector }
                        );
                    }
                }
            },
            slideoutHidden: (): void => {
                if (slideoutVisible) {
                    slideoutVisible = false;
                    const panel = this.availablePanels[this.currentOverlayPanelVariant ?? ""];
                    panel?.componentChangeDetector?.markForCheck();
                    if (
                        panel &&
                        state.template === panel.panelTemplate &&
                        panel.onVisibilityChange
                    ) {
                        afterNextRender(
                            () => {
                                panel!.onVisibilityChange?.(false);
                            },
                            { injector: this._injector }
                        );
                    }
                }
            }
        };

        this._overlayHolderApis[slideoutVariant][baseId] = api;
        return api;
    }
}
